import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable, signal } from "@angular/core";
import { BaseDataService, HttpResponsesHandlersFactory } from "@bbwt/modules/data-service";
import { SlotData } from "./slot-data";
import { ClinicSlotsService } from "@project/appointments/services/clinic-slot.service";
import { Slot } from "@project/appointments/models/slot";

@Injectable({
    providedIn: "root"
})
export class SlotDataService extends BaseDataService {
    readonly url = "api/clinic-slots";
    
    private _slotDataUpdated = signal<Slot>(null);
    readonly slotDataUpdated = this._slotDataUpdated.asReadonly();

    constructor(
        protected http: HttpClient,
        protected handlersFactory: HttpResponsesHandlersFactory,
        protected clinicSlotService: ClinicSlotsService
    ) {
        super(http, handlersFactory);
    }

    update(data: SlotData) {
        const promise = this.handleRequest<Slot>(
            this.http.put<Slot>(`${this.url}/update-booking`, data)
        );
        return promise
            .then(slot => {
                this._slotDataUpdated.set(slot);
                return Promise.resolve(slot);
            })
            .catch((errorResponse: HttpErrorResponse) => Promise.reject(errorResponse));
    }

    cancelBooking(slotDataId: string): Promise<Slot> {
        const promise = this.clinicSlotService.cancelBooking(slotDataId);
        return promise
            .then(slot => {
                this._slotDataUpdated.set(slot);
                return Promise.resolve(slot);
            })
            .catch((errorResponse: HttpErrorResponse) => Promise.reject(errorResponse));
    }

    markAsAvailable(slotDataId: string): Promise<Slot> {
        const promise = this.handleRequest<Slot>(
            this.http.put<Slot>(`${this.url}/${slotDataId}/available`, null)
        );
        return promise
            .then(slot => {
                this._slotDataUpdated.set(slot);
                return Promise.resolve(slot);
            })
            .catch((errorResponse: HttpErrorResponse) => Promise.reject(errorResponse));
    }

    markAsUnavailable(slotDataId: string): Promise<Slot> {
        const promise = this.handleRequest<Slot>(
            this.http.put<Slot>(`${this.url}/${slotDataId}/unavailable`, null)
        );
        return promise
            .then(slot => {
                this._slotDataUpdated.set(slot);
                return Promise.resolve(slot);
            })
            .catch((errorResponse: HttpErrorResponse) => Promise.reject(errorResponse));
    }

    splitSlot(slotDataId: string, numberOfSlots: number): Promise<boolean> {
        const promise = this.handleRequest<Slot[]>(
            this.http.post<Slot[]>(`${this.url}/${slotDataId}/split/${numberOfSlots}`, null)
        );
        return promise
            .then(slots => {
                this._slotDataUpdated.set(slots[0]);
                return Promise.resolve(true);
            })
            .catch((errorResponse: HttpErrorResponse) => Promise.reject(errorResponse));
    }

    bookAppointment(slotId: string, notes: string, rescheduleSlotId?: string, patientId?: string, newPatient?: boolean): Promise<Slot> {
        const data = { id: slotId, notes: notes, rescheduleSlotId: rescheduleSlotId, patientId: patientId, newPatient: newPatient };
        const promise = this.handleRequest<Slot>(
            this.http.put<Slot>(`${this.url}/book-appointment`, data)
        );
        return promise
            .then(slot => {
                this._slotDataUpdated.set(slot);
                return Promise.resolve(slot);
            })
            .catch((errorResponse: HttpErrorResponse) => Promise.reject(errorResponse));
    }

    get(id: string): Promise<Slot> {
        return this.handleRequest<Slot>(this.http.get<Slot>(`${this.url}/${id}`));
    }

    getSlotDuration(startTime: Date, endTime: Date): number | null {
        const start = new Date(startTime);
        const end = new Date(endTime);
        const duration = end.getHours() * 60 + end.getMinutes() - (start.getHours() * 60 + start.getMinutes());
        return duration;
    }
}
